import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StageScreen, StagePopup, popupHasContent } from './components/StageShell';
import { s3PublicUrl } from '../../utils/s3PublicUrl';

const CHUNK = 20;
const TOTAL = 100;
const AUTO_ADVANCE_DELAY_MS = 900;
// Matches legacy money/script.js: initialGDP = 131700000000000 ÷ 1e10 = 13.17 Lakh Crore
const DEFAULT_BASE_GDP = 13.17;

function formatGdp(value) {
  return `₹${value.toFixed(2)} Lakh Crore`;
}

export default function Stage3Economy({ stage, pagesBg, onComplete }) {
  const startHasContent = popupHasContent(stage?.start_popup_image, stage?.start_popup_text);
  const endHasContent = popupHasContent(stage?.end_popup_image, stage?.end_popup_text);
  const [phase, setPhase] = useState(startHasContent ? 'start' : 'game');
  const maxSectors = Math.min(5, Math.max(1, Number(stage?.max_sectors) || 5));
  const sectors = useMemo(
    () => (Array.isArray(stage?.sectors) ? stage.sectors : []).slice(0, maxSectors),
    [stage?.sectors, maxSectors]
  );

  const sectorKey = (sec, i) => sec?.label || `sector-${i}`;

  const [alloc, setAlloc] = useState(() =>
    Object.fromEntries(sectors.map((s, i) => [sectorKey(s, i), 0]))
  );
  const [picked, setPicked] = useState(false);
  const [touchDragging, setTouchDragging] = useState(false);
  const ghostRef = useRef(null);

  const used = Object.values(alloc).reduce((sum, v) => sum + v, 0);
  const remaining = TOTAL - used;

  // Use admin-configured base_gdp; fall back to the legacy constant (13.17 Lakh Crore)
  const baseGdp = Math.max(0, Number(stage?.base_gdp) || DEFAULT_BASE_GDP);
  const predictedGdp = baseGdp * (1 + used / 100);

  // Stable allocator — reads remaining inside the setState callback so the
  // effect closure never goes stale.
  const allocateTo = useCallback((key) => {
    setAlloc((a) => {
      const totalUsed = Object.values(a).reduce((sum, v) => sum + v, 0);
      const rem = TOTAL - totalUsed;
      if (rem <= 0) return a;
      const add = Math.min(CHUNK, rem);
      return { ...a, [key]: (a[key] || 0) + add };
    });
    setPicked(false);
  }, []);

  const resetSector = (key, e) => {
    e?.stopPropagation();
    setAlloc((a) => ({ ...a, [key]: 0 }));
  };

  useEffect(() => {
    if (remaining <= 0) {
      const t = setTimeout(() => {
        if (endHasContent) setPhase('end');
        else onComplete({ alloc, predictedGdp });
      }, AUTO_ADVANCE_DELAY_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [remaining, endHasContent, onComplete, alloc, predictedGdp]);

  // ── Touch drag: money bag → sector ──────────────────────────────────────────

  const onBagTouchStart = useCallback((e) => {
    if (remaining <= 0) return;
    e.preventDefault();
    setPicked(false);
    setTouchDragging(true);

    const touch = e.touches[0];
    const bagEl = e.currentTarget;
    const rect = bagEl.getBoundingClientRect();
    const imgEl = bagEl.querySelector('img');

    const ghost = document.createElement('div');
    ghost.style.cssText = `
      position:fixed;pointer-events:none;z-index:9999;
      width:${rect.width}px;height:${Math.min(rect.height, 90)}px;
      transform:translate(-50%,-50%);
      left:${touch.clientX}px;top:${touch.clientY}px;
      background:rgba(0,20,60,0.88);
      border:2px solid rgba(100,200,255,0.85);
      border-radius:10px;
      display:flex;align-items:center;justify-content:center;
      box-shadow:0 6px 20px rgba(0,0,0,0.55);
    `;
    if (imgEl) {
      const img = document.createElement('img');
      img.src = imgEl.src;
      img.style.cssText = 'width:48px;height:auto;object-fit:contain;';
      ghost.appendChild(img);
    }
    document.body.appendChild(ghost);
    ghostRef.current = ghost;
  }, [remaining]);

  useEffect(() => {
    if (!touchDragging) return;

    const clearHighlight = () => {
      document.querySelectorAll('[data-eco-sector]').forEach((s) =>
        s.classList.remove('fi-economy-sector--touch-over')
      );
    };

    const onMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      if (ghostRef.current) {
        ghostRef.current.style.left = touch.clientX + 'px';
        ghostRef.current.style.top = touch.clientY + 'px';
        ghostRef.current.style.visibility = 'hidden';
      }
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghostRef.current) ghostRef.current.style.visibility = '';

      clearHighlight();
      const sectorEl = el?.closest('[data-eco-sector]');
      if (sectorEl) sectorEl.classList.add('fi-economy-sector--touch-over');
    };

    const onEnd = (e) => {
      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHighlight();

      const touch = e.changedTouches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const sectorEl = el?.closest('[data-eco-sector]');
      if (sectorEl) allocateTo(sectorEl.dataset.ecoSector);

      setTouchDragging(false);
    };

    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);

    return () => {
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      if (ghostRef.current) { ghostRef.current.remove(); ghostRef.current = null; }
      clearHighlight();
    };
  }, [touchDragging, allocateTo]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === 'start') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_3" stage={stage}>
        <StagePopup image={stage?.start_popup_image} text={stage?.start_popup_text} onNext={() => setPhase('game')} />
      </StageScreen>
    );
  }

  if (phase === 'end') {
    return (
      <StageScreen pagesBg={pagesBg} pageKey="stage_3" stage={stage}>
        <StagePopup
          image={stage?.end_popup_image}
          boldText={formatGdp(predictedGdp)}
          text={stage?.end_popup_text}
          onNext={() => onComplete({ alloc, predictedGdp })}
        />
      </StageScreen>
    );
  }

  const bagImg = s3PublicUrl(stage?.money_bag_image);
  const coinImg = s3PublicUrl(stage?.coin_image);

  return (
    <StageScreen pagesBg={pagesBg} pageKey="stage_3" stage={stage}>
      <div className="fi-stage-root fi-economy-root">
        <div className="fi-economy-header">
          <div className="fi-economy-badge">{stage?.title || 'Economy'}</div>
          <p className="fi-economy-tagline">Let&rsquo;s talk numbers.</p>
          {stage?.subtitle ? <p className="fi-economy-question">{stage.subtitle}</p> : null}
        </div>

        <div className="fi-economy-panel">
          <p className="fi-economy-instruction">
            {stage?.instruction || 'Drag and drop the investments to each sector'}
          </p>
          <p className="fi-economy-live-gdp">India&rsquo;s GDP in 2047 will be: {formatGdp(predictedGdp)}</p>

          <div className="fi-economy-row">
            <div
              className={`fi-economy-bag ${picked ? 'fi-economy-bag--picked' : ''} ${remaining <= 0 ? 'fi-economy-bag--empty' : ''}`}
              role="button"
              tabIndex={0}
              draggable={remaining > 0}
              onClick={() => remaining > 0 && setPicked((p) => !p)}
              onKeyDown={(e) => { if (e.key === 'Enter' && remaining > 0) setPicked((p) => !p); }}
              onDragStart={(e) => {
                // Tap-to-pick and native drag are two separate ways to move money into a
                // sector — they must never be active at the same time. If "picked" were
                // still true from an earlier tap while a drag-and-drop also completes, a
                // stray click firing on the drop target afterward (some Android WebViews
                // do this) would trigger the sector's onClick allocation *in addition to*
                // the drop's, silently deducting two chunks for what looked like one drag.
                setPicked(false);
                e.dataTransfer.setData('text/plain', 'coin');
                e.dataTransfer.effectAllowed = 'move';
              }}
              onTouchStart={onBagTouchStart}
            >
              <div className="fi-economy-pct">{remaining}%</div>
              <div className="fi-economy-icon fi-economy-icon--bag">
                {bagImg ? <img src={bagImg} alt="Money bag" /> : <span className="fi-money-bag-emoji">💰</span>}
              </div>
              <div className="fi-economy-label">{picked ? 'Tap a sector' : 'Tap or drag'}</div>
            </div>

            {sectors.map((sec, i) => {
              const key = sectorKey(sec, i);
              const value = alloc[key] || 0;
              const coins = Math.round(value / CHUNK);
              return (
                <div
                  key={key}
                  data-eco-sector={key}
                  className={`fi-economy-sector ${picked ? 'fi-economy-sector--droppable' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => (picked ? allocateTo(key) : null)}
                  onKeyDown={(e) => { if (e.key === 'Enter') allocateTo(key); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                  onDrop={(e) => { e.preventDefault(); allocateTo(key); }}
                >
                  <div className="fi-economy-pct">{value}%</div>
                  <div className="fi-economy-visual">
                    <div className="fi-economy-icon">
                      {sec.image ? <img src={s3PublicUrl(sec.image)} alt={sec.label} /> : null}
                    </div>
                    {coinImg ? (
                      <div className="fi-economy-coins">
                        {Array.from({ length: coins }).map((_, c) => (
                          <img key={c} src={coinImg} alt="" />
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <div className="fi-economy-label">{sec.label}</div>
                  {sec.text ? <div className="fi-economy-text">{sec.text}</div> : null}
                  {value > 0 ? (
                    <button type="button" className="fi-economy-reset" onClick={(e) => resetSector(key, e)}>
                      reset
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StageScreen>
  );
}
